'use client';
import { proPath } from '@/lib/proUrl.mjs'; // Founder 06/10/26: a pro's URL is ONLY its number
import { getSessionId } from '@/lib/trackerSession'; // one tracker id, created if missing (lead ↔ visit, Founder 2026-09-27)
// The ONE persistent instance of the QuickConsult bottom bar — rendered once from
// SiteShell.tsx (outside routed page content) so it survives client-side navigation
// (2026-07-23 user request: clicking a shown pro card navigates to that pro's page while
// this bar stays mounted exactly as it was, city/pros/CTA state intact, only the URL
// underneath changes). Base data (which article seeded it, its pros/variants) comes from
// lib/quickConsultStore.tsx, pushed in by the per-article "seeder" (QuickConsultClient.tsx).
// Local interactive state below persists automatically simply because this component
// never unmounts — no extra plumbing needed for that part.
import Link from 'next/link';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuickConsult, setConsultDraft } from '@/lib/quickConsultStore';
import { useSmartIntakeCtx, notifySmartIntake, resolvePageCity } from '@/lib/smartIntakeStore';
import { popularCities, findCity } from '@/lib/israelCities';
import { MIN_MATCHES, matchProsToCity, rankProsForCity } from '@/lib/proMatching';
import { logCityDemandGap } from '@/lib/logCityDemandGap';
import { loadGoogleMaps } from '@/lib/googleMaps';
import { pickVariantIndex } from '@/lib/ctaVariantsShared';
import { logCtaImpression, logCtaClick } from '@/lib/ctaTracking';
import { logInquiry } from '@/lib/inquiries';
import { fireContactIntent } from '@/lib/contactTracking';
import { openCompanyWhatsApp } from '@/lib/companyWa';
import { contactViaCompany } from '@/lib/directContact';
import { buildInquiryMessage } from '@/lib/inquiryMessage';
import EveningNote from '@/components/EveningNote';
import { currentGreeting } from '@/lib/eveningClient';
import { buildWaSendMessage, type WaCtx } from '@/lib/waMessage';
import { fmtRating } from '@/lib/format';
import WaIcon from './WaIcon';
import QuestionnaireWizard from './QuestionnaireWizard';
import { SMART_INTAKE_KEY, type SmartIntakeContext } from './SmartIntakeBar';
import type { QuickConsultPro } from '@/lib/quickConsultTypes';
import { avatarImg } from '@/lib/imgUrl';

export type { QuickConsultPro };

// 2026-08-19 (user directive): synced to the REAL number of experts the WhatsApp message
// actually links to — lib/waLinks.ts's `opts.limit ?? 3` (was briefly 5 to match that
// function's outer cap of 5, but the default/actual send is 3 — was 8 originally). This
// preview row (matched-pros carousel shown during/after the questionnaire) must never
// promise more experts than the real message delivers. Distance-first priority (same
// city, else nearest) already comes from matchProsToCity/applyCity's sort — unchanged.
const PROS_SHOWN = 3;
const CITIES_SHOWN = 50;

function getTrackerSessionId(): string {
  try {
    let id = getSessionId();
    if (!id) id = String(Math.random());
    return id;
  } catch { return String(Math.random()); }
}

export default function QuickConsultBar() {
  const { state } = useQuickConsult();
  const { active, articleId, categorySlug, categoryName, categoryNamePlural, pros, singlePro, variants, bgAvatars, preciseQuestionnaire, skipCityQuestion, cityLast, seedKey, presetCity } = state;

  // 2026-08-19 (user report: on the base /שמאי-מקרקעין page — no city in the URL — the
  // visible list above already said "מצאנו 3 מומחים בחדרה" (it reads the shared
  // smartIntakeCtx store, which still had Hadera from an earlier page in the session —
  // see CategoryExpertsList.tsx's intentional "no page-specific city → trust whatever the
  // store already knows" fallback), but the QUESTIONNAIRE right below it still asked for
  // a city from scratch, ignoring that same already-known answer). The wizard only ever
  // knew about a HARD, URL-derived presetCity — it had no way to see the store's answer.
  // `knownCity` is presetCity when the URL provides one (always wins), else whatever
  // city the shared store already has on file — the exact same source of truth the
  // visible list above already uses, so the two stop contradicting each other.
  const intakeCtx = useSmartIntakeCtx();
  const knownCity = useMemo(() => presetCity || (
    intakeCtx?.city && typeof intakeCtx.cityLat === 'number' && typeof intakeCtx.cityLng === 'number'
      ? { name: intakeCtx.city, lat: intakeCtx.cityLat, lng: intakeCtx.cityLng }
      : null
  ), [presetCity, intakeCtx?.city, intakeCtx?.cityLat, intakeCtx?.cityLng]);
  // seeding (not a visitor pick): apply THE page city (lib/smartIntakeStore resolvePageCity) — never write the URL city over a
  // saved active choice (Back/Forward) or a newer one (bughunter round 3 #3)
  function seedCity(k: { name: string; lat: number; lng: number }) {
    const rc = resolvePageCity() || k.name;
    if (rc === k.name) { applyCity(k.name, { lat: k.lat, lng: k.lng }); return; }
    const f = findCity(rc); applyCity(rc, f ? { lat: f.lat, lng: f.lng } : undefined);
  }
  // 2026-07-29 (user request): the questionnaire is the DEFAULT state — the bar opens
  // straight into the first question instead of the passive "search theater" + a direct
  // WA button. 'search' mode still exists as the fallback for categories with no
  // questionnaire, and as the browse view if the visitor backs out of the questions.
  const [mode, setMode] = useState<'search' | 'questions'>('search');
  // CLS (30/09/26, measured 0.09–0.12 at 1280): a new seed painted ONE frame in the shorter 'search' shape before the seed effect
  // below switched to 'questions' — the fixed bar grew ~43px after first paint. The mode for a new seed is set during render
  // (React's adjust-state-while-rendering pattern), so the first painted frame already has the final shape.
  const [modeSeed, setModeSeed] = useState<string | undefined>(undefined);
  if (active && modeSeed !== seedKey) { setModeSeed(seedKey); setMode(preciseQuestionnaire ? 'questions' : 'search'); }
  const [preciseCtx, setPreciseCtx] = useState<Partial<SmartIntakeContext> | null>(null);
  // Bumped whenever the visitor resets their city (CategoryExpertsList's "🔄 בחירת עיר
  // אחרת" chip, see lib/smartIntakeStore.ts's resetCity()) — used as the questionnaire's
  // `key` below so it fully remounts with fresh step/answers instead of silently
  // reshuffling mid-flow once the now-missing city question gets re-inserted.
  const [cityResetNonce, setCityResetNonce] = useState(0);

  // Reviews V5 (30/09/26): the word is always "המלצות" — no preview switch any more.
  // (see the questions-mode slot below) — the labels then name the city instead of waiting for one.
  const [cityName, setCityName] = useState<string | null>(null);
  const [narrowing, setNarrowing] = useState(false);
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherText, setOtherText] = useState('');
  const barRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const acRef = useRef<any>(null);
  const [shownPros, setShownPros] = useState<QuickConsultPro[]>(() => singlePro ? [singlePro] : pros.slice(0, PROS_SHOWN));

  // A NEW context seeded the bar (different seedKey than last time — an article, a
  // category page, or a specific pro's page, see quickConsultStore.tsx) — reset the
  // interactive state to that context's own fresh defaults. Re-seeding the SAME context
  // (e.g. a Strict Mode double-effect) must NOT reset anything the visitor already did.
  // 2026-07-29: was keyed on `articleId` alone — broke the moment category/pro pages
  // started seeding too, since articleId is always undefined there and `undefined ===
  // undefined` never satisfied the old "already this one" guard, wiping every answer on
  // every category↔category or pro↔pro navigation.
  const lastSeedKey = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (lastSeedKey.current === seedKey) return;
    lastSeedKey.current = seedKey;
    setNarrowing(false);
    setOtherOpen(false);
    setOtherText('');
    // Questionnaire-first (2026-07-29): open in questions mode whenever this context
    // has one. On the very first seed `active` just flipped true, so this also covers
    // the initial page load, not only client-side navigations between contexts.
    setMode(preciseQuestionnaire ? 'questions' : 'search');
    setPreciseCtx(null);
    // 2026-08-23 (user report): pro A → category → pro B left the avatar preview strip
    // showing A. Root cause: this used to only reset `shownPros` when `!knownCity` — if a
    // city was ALREADY known (from an earlier visit, unrelated to A vs B), this branch
    // was skipped entirely, and the OTHER effect that recomputes `shownPros` for a known
    // city only re-fires when `knownCity?.name` itself changes, which it doesn't just
    // because the pro changed. Single-pro mode (this bar scoped to one specific
    // professional's own page) must ALWAYS snap `shownPros` to that pro right away,
    // regardless of city — matching mode's own city-independent behavior in applyCity()
    // above (single-pro never re-matches by city either).
    if (singlePro) {
      setShownPros([singlePro]);
    } else if (!knownCity) {
      setCityName(null);
      setShownPros(pros.slice(0, PROS_SHOWN));
    } else {
      // Founder 2026-09-22: back from a professional's page (יערה) to the category with a known city — the bar
      // used to keep showing that single pro; now it re-matches the category's pros to the known city.
      setShownPros(pros.slice(0, PROS_SHOWN));
      seedCity(knownCity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey, preciseQuestionnaire]);

  // 2026-08-19 bug fix (user report: after finishing the questionnaire on
  // /אינסטלטור/נתניה, the recommended experts weren't from Netanya at all). Root
  // cause: this bar's OWN cityName/shownPros preview state was only ever set by
  // applyCity(), called from `onCityAnswered` — which fires when the wizard's CITY
  // QUESTION is live-answered. On a /{category}/{city} page the city question is
  // skipped entirely (already known from the URL, via presetCity) — so onCityAnswered
  // NEVER fired, cityName stayed null the whole time, and shownPros silently defaulted
  // to the category's top-RATED pros site-wide (unfiltered by any city) instead of
  // Netanya-matched ones. Now: whenever this context has a known city (URL presetCity,
  // or — 2026-08-19 follow-up — a city already on file in the shared store even with no
  // URL city, see `knownCity` above), seed the SAME matching applyCity() would do for a
  // live answer. Deliberately a SEPARATE effect from the seedKey-gated reset above: the
  // shared store hydrates asynchronously (see the city-flash bug fixed earlier), so
  // `knownCity` can still be null the instant the seed effect first runs and only
  // resolve a beat later — this effect re-fires on its own the moment that happens,
  // instead of missing it because the seed effect already ran once and won't again.
  useEffect(() => {
    if (knownCity) seedCity(knownCity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knownCity?.name]);

  // 2026-08-22 (user request): the "🔄 בחירת עיר אחרת" chip on the category page
  // (CategoryExpertsList.tsx) clears the shared store's city via resetCity() — react to
  // that HERE by reopening the questionnaire at its city question, not just letting
  // `knownCity` silently go null. Only fires on a real known→unknown transition (never on
  // the initial mount, and never on a /{category}/{city} page — there `presetCity` always
  // wins in `knownCity` above regardless of the store, so the chip doesn't even render).
  const prevKnownCityRef = useRef<string | null>(null);
  useEffect(() => {
    const was = prevKnownCityRef.current;
    const now = knownCity?.name ?? null;
    if (was && !now && !presetCity) {
      setCityName(null);
      setNarrowing(false);
      setPreciseCtx(null);
      setShownPros(singlePro ? [singlePro] : pros.slice(0, PROS_SHOWN));
      if (preciseQuestionnaire) setMode('questions');
      setCityResetNonce(n => n + 1); // forces QuestionnaireWizard to remount at step 0
    }
    prevKnownCityRef.current = now;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knownCity?.name]);

  // Reserve scroll room + export --wa-bar-h (same technique as CompactWaBar.tsx, which
  // this replaces on article pages).
  useEffect(() => {
    if (!active) return;
    const apply = () => {
      const h = barRef.current?.offsetHeight || 0;
      document.body.style.paddingBottom = h ? `${h}px` : '';
      document.documentElement.style.setProperty('--wa-bar-h', h ? `${h}px` : '0px');
    };
    apply();
    const ro = new ResizeObserver(apply);
    if (barRef.current) ro.observe(barRef.current);
    return () => { document.body.style.paddingBottom = ''; document.documentElement.style.removeProperty('--wa-bar-h'); ro.disconnect(); };
  }, [active]);


  const variant = useMemo(() => {
    if (!variants.length) return null;
    const idx = pickVariantIndex(getTrackerSessionId() + '|quick_consult', variants.length);
    return variants[idx];
  }, [variants]);

  useEffect(() => {
    if (variant) logCtaImpression(variant.id, articleId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant?.id, articleId]);

  function applyCity(name: string, coords?: { lat: number; lng: number }) {
    setCityName(name);
    if (categorySlug) setConsultDraft({ categorySlug, city: name }); // consult link (02/10/26)
    setOtherOpen(false);
    setNarrowing(true);
    const resolved = coords || (findCity(name) ? { lat: findCity(name)!.lat, lng: findCity(name)!.lng } : null);
    // 2026-08-19 (user report): picking a city from these quick chips only ever updated
    // this bar's OWN small preview row — it never touched the shared smartIntakeCtx store,
    // so the real expert list below (CategoryExpertsList, a completely separate component)
    // never found out and kept showing whatever it started with. This is a genuine city
    // pick (same intent as answering the questionnaire's city question), so it gets written
    // through exactly like QuestionnaireWizard's savePartialCtx does — including the
    // cityAnsweredAt stamp CategoryExpertsList requires to trust it as fresh.
    try {
      const existing = localStorage.getItem(SMART_INTAKE_KEY);
      const prev = existing ? JSON.parse(existing) : {};
      if (!prev.categorySlug || prev.categorySlug === categorySlug) {
        localStorage.setItem(SMART_INTAKE_KEY, JSON.stringify({
          ...prev,
          categorySlug,
          categoryName: prev.categoryName || categoryName,
          city: name,
          cityLat: resolved?.lat ?? null,
          cityLng: resolved?.lng ?? null,
          cityAnsweredAt: Date.now(),
          cityChosenAt: prev.city === name ? prev.cityChosenAt ?? null : null, // not a choice by itself (re-verify #6)
        })); notifySmartIntake();
      }
    } catch {}
    setTimeout(() => {
      // Single-pro mode (seeded from a specific professional's own page, 2026-08-22
      // fix) — NEVER re-match by city here. The referral always goes to this one
      // professional regardless of which city the visitor picks; re-matching against
      // the category-wide `pros` pool is exactly what showed a different pro's name/
      // photo than the page the visitor was actually on.
      if (singlePro) {
        setShownPros([singlePro]);
      } else if (resolved && !cityLast) { // nationwide category (cityLast): every pro serves every city — no radius narrowing
        // Canonical adaptive-radius engine (2026-07-29, lib/proMatching.ts) — expands
        // past the flat 40km default when the city has no pros that close, instead of
        // silently showing nothing; logs the gap so /admin/category-supply can surface
        // exactly which cities have real demand but no nearby supply.
        const { matched, radiusKm, expanded } = matchProsToCity(pros, resolved.lat, resolved.lng, MIN_MATCHES, name);
        // 2026-08-23 (user directive): closest-first everywhere, rating only as a
        // tiebreak — was rating-only, which could show a farther pro ahead of a closer
        // one within the same matched radius. Matches CategoryExpertsList's own sort.
        // Founder 2026-09-28: same city first, then nearest, rating last — the ONE rule (lib/proMatching rankProsForCity).
        setShownPros(rankProsForCity(matched, { name, lat: resolved.lat, lng: resolved.lng }).slice(0, PROS_SHOWN));
        if (expanded) logCityDemandGap(categorySlug, name, radiusKm, expanded, matched.length);
      } else if (resolved) { // nationwide: nobody is filtered out, but the visitor's city still orders the strip
        setShownPros(rankProsForCity(pros, { name, lat: resolved.lat, lng: resolved.lng }, { locationIndependent: true }).slice(0, PROS_SHOWN)); // Founder 04/10: rating × reviews
      } else {
        setShownPros(pros.slice(0, PROS_SHOWN));
      }
      setNarrowing(false);
    }, 450);
  }

  useEffect(() => {
    if (!otherOpen) return;
    loadGoogleMaps(() => {
      if (!inputRef.current || acRef.current || !window.google?.maps?.places) return;
      try {
        const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
          types: ['(cities)'], componentRestrictions: { country: 'il' }, fields: ['name', 'geometry'],
        });
        ac.addListener('place_changed', () => {
          const p = ac.getPlace();
          const name = (p?.name || inputRef.current?.value || '').trim();
          const loc = p?.geometry?.location;
          if (name) applyCity(name, loc ? { lat: loc.lat(), lng: loc.lng() } : undefined);
        });
        acRef.current = ac;
        inputRef.current.focus();
      } catch {}
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherOpen]);

  async function handleSend() {
    if (variant) logCtaClick(variant.id, articleId);
    fireContactIntent(singlePro?.id, singlePro ? singlePro.firstName : 'שאלון', categorySlug, 'whatsapp'); // 2026-09-26: the questionnaire never fired a conversion intent (click ids) — Ads saw nothing
    const ticketNo = await logInquiry({
      proId: singlePro?.id, // a ticket is ALWAYS linked to its pro (bughunter round 4 B: the worker sent a multi-match intro + a false gap alert)
      source: 'quick_consult',
      articleId,
      categorySlug,
      city: cityName || undefined,
      questionnaireAnswered: !!cityName,
      ctaVariantId: variant?.id,
    });
    // Single-pro mode (2026-08-22 fix) — same rule as handlePreciseSend: a category
    // with no questionnaire still must not send this pro page's "quick" button to the
    // generic category-wide flow.
    if (singlePro) {
      const waMessage = buildWaSendMessage(singlePro.firstName, { categorySlug, categoryName, city: cityName || null }, categoryName);
      await contactViaCompany({
        proId: singlePro.id, proGender: singlePro.gender, displayName: singlePro.firstName,
        categorySlug, categoryName, intent: 'whatsapp', waMessage, ticket: ticketNo, city: cityName || null,
      });
      return;
    }
    const message = buildInquiryMessage({ categoryName, city: cityName || undefined, ticketNo });
    openCompanyWhatsApp(message);
    // 2026-07-29 user request: no "נשלח" swap — the button stays as-is after clicking.
  }

  // "🎯 שאלות לייעוץ מדויק" — QuestionnaireWizard (condensed) collects the answers and
  // hands back a ctx (same shape the rest of the site's questionnaires use). The WA
  // message itself uses a fixed, friendly opening line (user request) rather than the
  // per-category buildWaMessage clause — the FULL structured answer set still goes into
  // he_inquiries.questionnaire_answers either way, so nothing is lost, just not spelled
  // out in the visible WhatsApp text.
  function handlePreciseComplete(ctx: Partial<SmartIntakeContext>) {
    setPreciseCtx(ctx);
    if (categorySlug) setConsultDraft({ categorySlug, need: ctx.cleanNeed || ctx.customNeed || null, ...(ctx.city ? { city: ctx.city } : {}) }); // consult link (02/10/26)
  }

  // "→ שינוי תשובות" on the ready step: back to Q1 with the previous answers cleared.
  function handlePreciseRestart() {
    setPreciseCtx(null);
    if (categorySlug) setConsultDraft({ categorySlug, need: null });
    setMode('questions');
    setCityResetNonce(n => n + 1); // remounts QuestionnaireWizard at step 0 (fresh answers)
  }

  async function handlePreciseSend() {
    if (!preciseCtx) return;
    if (variant) logCtaClick(variant.id, articleId);
    fireContactIntent(singlePro?.id, singlePro ? singlePro.firstName : 'שאלון', categorySlug, 'whatsapp'); // 2026-09-26: the questionnaire never fired a conversion intent (click ids) — Ads saw nothing
    const ticketNo = await logInquiry({
      proId: singlePro?.id, // a ticket is ALWAYS linked to its pro (bughunter round 4 B: the worker sent a multi-match intro + a false gap alert)
      source: 'hook_questionnaire',
      articleId,
      categorySlug,
      city: preciseCtx.city || undefined,
      questionnaireAnswered: true,
      questionnaireAnswers: preciseCtx as Record<string, unknown>,
      ctaVariantId: variant?.id,
    });

    // Single-pro mode (seeded from a specific professional's own page, 2026-08-22
    // fix) — the referral must go ONLY to this professional, never the category-wide
    // matched pros the generic flow below uses. Reuses the exact same tracked-link +
    // "message goes to OUR company WhatsApp, never straight to the pro" engine every
    // other contact button on the site already uses (lib/directContact.ts) — see its
    // own doc comment for why that routing is a hard rule, not a shortcut.
    if (singlePro) {
      const waMessage = buildWaSendMessage(singlePro.firstName, preciseCtx as WaCtx, categoryName);
      await contactViaCompany({
        proId: singlePro.id, proGender: singlePro.gender, displayName: singlePro.firstName,
        categorySlug, categoryName, intent: 'whatsapp', waMessage, ticket: ticketNo, city: preciseCtx.city || null, need: preciseCtx.cleanNeed || null,
      });
      return;
    }

    // WA short-link flow (2026-07-29, lib/waLinks.ts): create tracked /wa/<code> links
    // for the top matched pros and send the visitor a composed message to OUR company
    // number that carries those links — we get their phone + full context, they get
    // one-tap links to each pro in their own chat history. Falls back to the plain
    // inquiry message if the API hiccups — never block the send on it.
    let message: string | null = null;
    try {
      // 2026-07-31: link this inquiry's he_wa_links rows back to the visitor's
      // journey — sessionId (SmartIntake/questionnaire) + trackerSessionId (ex_sid,
      // he_sessions/he_user_events), same ids logPull() already threads elsewhere.
      let sessionId: string | null = null;
      try {
        const saved = localStorage.getItem(SMART_INTAKE_KEY);
        if (saved) sessionId = JSON.parse(saved)?.sessionId ?? null;
      } catch {}
      const res = await fetch('/api/wa-links', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          categorySlug,
          city: preciseCtx.city || null,
          needClause: preciseCtx.cleanNeed || preciseCtx.customNeed || null,
          answers: preciseCtx.answers || [],
          ticketNo, articleId, greeting: currentGreeting(),
          sessionId, trackerSessionId: getTrackerSessionId(),
        }),
      });
      const d = await res.json().catch(() => null);
      if (d?.ok && d.visitorMessage) message = d.visitorMessage;
    } catch {}
    if (!message) {
      // 2026-09-27 (bughunter): the fallback used to drop the category and the answers and double "אשמח" — same
      // composer, same facts as the API path: category + composed need + city + ticket.
      message = buildInquiryMessage({
        categoryName,
        city: preciseCtx.city || undefined,
        needClause: preciseCtx.cleanNeed || preciseCtx.customNeed || null,
        ticketNo,
      });
    }
    openCompanyWhatsApp(message);
    // 2026-07-29 user request: the button stays exactly as it was after the click — no
    // "נשלח בהצלחה" replacement state (visitor may want to re-open WhatsApp).
  }

  const cities = useMemo(() => popularCities(CITIES_SHOWN), []);
  const cityChipStyle: React.CSSProperties = {
    whiteSpace: 'nowrap', padding: '4px 10px', borderRadius: 999,
    border: '1.5px solid #e0f2fe', background: '#f0f9ff', color: '#0369a1',
    fontWeight: 700, fontSize: 11.5, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
  };
  const MIN_MARQUEE = 30;

  // ?layout= preview bars (Founder 01/10/26, lib/topicLayout.ts) — seeded only by a preview URL; no barVariant = the standard bar, untouched.
  // 'one'/'row' = compact questionnaire rows; 'pill' = a collapsed pill that opens THIS full bar on tap (same wizard, same state).
  const barVariant = state.barVariant;
  const [pillOpen, setPillOpen] = useState(false);
  useEffect(() => { setPillOpen(false); }, [seedKey]);
  // 3.6 Mad-Libs: the wizard's questions + answer labels; a blank → jump the wizard there inside the sheet; answered → the sheet closes
  type MlProg = { step: number; items: { id: string; isCity: boolean; question: string; label: string | null; options?: number }[] };
  const [mlProg, setMlProg] = useState<MlProg | null>(null);
  const [mlJump, setMlJump] = useState<{ step: number; n: number } | null>(null);
  const mlOpenedStep = useRef<number | null>(null);
  const mlOpenedLabel = useRef<string | null>(null);
  const mlLanded = useRef(false);
  const jumpN = useRef(0);
  // a jump is consumed once, then cleared — a later wizard remount (city reset / restart) never replays it
  const fireJump = useCallback((step: number) => { jumpN.current += 1; setMlJump({ step, n: jumpN.current }); setTimeout(() => setMlJump(null), 350); }, []);
  const onMlProgress = useCallback((p: MlProg) => {
    setMlProg(p);
    // a blank-opened sheet closes once THAT question got an (other) answer — the same rule for every blank (bughunter 02/10 #2)
    const op = mlOpenedStep.current;
    if (op == null) return;
    if (p.step === op) mlLanded.current = true; // the jump arrived (goToStep is async) — only a step change AFTER that counts
    const answered = !!p.items[op]?.label && p.items[op]?.label !== mlOpenedLabel.current;
    if (answered || (mlLanded.current && p.step !== op)) { mlOpenedStep.current = null; mlLanded.current = false; setPillOpen(false); }
  }, []);
  // consult link (02/10/26): answers so far → ConsultFloat's prefill (never re-renders anything; effect fires on answer change only)
  const onWizardProgress = useCallback((pp: MlProg) => {
    if (categorySlug) {
      const need = pp.items.filter(it => !it.isCity && it.label).map(it => it.label).join(', ') || null;
      const cityIt = pp.items.find(it => it.isCity && it.label);
      setConsultDraft({ categorySlug, need, ...(cityIt?.label ? { city: cityIt.label } : {}) });
    }
    if (barVariant) onMlProgress(pp);
  }, [categorySlug, barVariant]); // eslint-disable-line react-hooks/exhaustive-deps
  // (mlProg is NOT cleared here: the child wizard's mount effect reports first and a parent clear would wipe it — the remounted wizard overwrites it)
  useEffect(() => { setMlJump(null); mlOpenedStep.current = null; }, [seedKey]);
  // the list's "בחירת עיר / החלף עיר" → open the bar AND jump the wizard to its city question
  const mlProgRef = useRef<MlProg | null>(null); mlProgRef.current = mlProg;
  useEffect(() => {
    const toCity = () => {
      const i = mlProgRef.current?.items.findIndex(it => it.isCity) ?? -1;
      if (i < 0) return;
      setTimeout(() => { mlOpenedStep.current = barVariant === 'madlibs' || barVariant === 'sheetpill' || barVariant === 'sheetfooter' ? i : null; mlOpenedLabel.current = mlProgRef.current?.items[i]?.label || null; mlLanded.current = mlProgRef.current?.step === i; fireJump(i); }, 60);
    };
    window.addEventListener('experts:open-bar', toCity);
    return () => window.removeEventListener('experts:open-bar', toCity);
  }, [barVariant]);
  // sheet: Escape closes, focus moves into it on open (keyboard reachable)
  const sheetRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!pillOpen) return;
    const sheet = barVariant === 'sheetpill' || barVariant === 'sheetfooter' || barVariant === 'madlibs';
    if (!sheet) return;
    const opener = document.activeElement as HTMLElement | null; // focus returns here on close (bughunter #6)
    const t = setTimeout(() => { const el = sheetRef.current?.querySelector<HTMLElement>('button[aria-pressed], input, button'); (el || sheetRef.current)?.focus(); }, 200);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPillOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); if (opener && document.contains(opener) && opener.offsetParent !== null) opener.focus(); else (document.querySelector<HTMLElement>('[data-bar-pill], [data-madlibs] button'))?.focus(); };
  }, [pillOpen, barVariant]);
  useEffect(() => {
    const open = () => setPillOpen(true); // the list's "החלף עיר" asks the bar to open (CategoryExpertsList compact count line)
    window.addEventListener('experts:open-bar', open);
    return () => window.removeEventListener('experts:open-bar', open);
  }, []);
  if (!active) return null;

  const headerGradient = 'var(--sk-band, linear-gradient(135deg, #0d47a1 0%, #1565c0 60%, #1976d2 100%))';

  // ?layout= bars. 2.x: 'one'/'row' compact rows, 'pill' = collapsed pill → this full bar. 3.x (bezalel spec): 'seg' = hairline segmented
  // track; 'sheetpill'/'sheetfooter' = a trigger that opens a modal bottom sheet of option rows; 'madlibs' = a sentence whose blanks open
  // that sheet at their own question. The wizard stays MOUNTED while collapsed (hidden), so answers survive collapse/expand.
  const madlibs = barVariant === 'madlibs' && mode === 'questions';
  const sheetMode = mode === 'questions' && (barVariant === 'sheetpill' || barVariant === 'sheetfooter' || barVariant === 'madlibs');
  const v3 = barVariant === 'seg' || barVariant === 'sheetpill' || barVariant === 'sheetfooter' || barVariant === 'madlibs';
  const compactBar = mode === 'questions' && (barVariant === 'one' || barVariant === 'row' || barVariant === 'seg') ? barVariant : null;
  const wizardLayout: 'one' | 'row' | 'seg' | 'list' | undefined = compactBar || (sheetMode ? 'list' : undefined);
  const pillMode = barVariant === 'pill' && mode === 'questions';
  const collapsed = (pillMode || sheetMode) && !pillOpen;
  const firstQ = preciseQuestionnaire?.questions?.[0];
  const curQ = mlProg && mlProg.step < mlProg.items.length ? mlProg.items[mlProg.step] : null; // the wizard's CURRENT question (bughunter #5)
  const pillLabel = preciseCtx ? 'הפנייה מוכנה לשליחה' : (curQ?.question || firstQ?.question || `ייעוץ מהיר עם ${categoryNamePlural || categoryName}`);
  const pillCount = preciseCtx ? 0 : curQ ? (curQ.isCity ? 0 : curQ.options || 0) : (firstQ?.options?.length || 0);
  const totalQ = mlProg?.items.length || ((preciseQuestionnaire?.questions?.length || 0) + (skipCityQuestion ? 0 : 1));
  const chevUp = <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}><path d="M6 15l6-6 6 6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
  const openBlank = (idx: number) => { mlOpenedStep.current = idx; mlOpenedLabel.current = mlProgRef.current?.items[idx]?.label || null; mlLanded.current = (mlProgRef.current?.step === idx); fireJump(idx); if (preciseCtx) setPreciseCtx(null); setPillOpen(true); };
  const mlItems = mlProg?.items || [];
  const mlOrdered = [...mlItems.map((it, i) => ({ ...it, i })).filter(it => !it.isCity), ...mlItems.map((it, i) => ({ ...it, i })).filter(it => it.isCity)];
  const blank = (it: { id: string; i: number; label: string | null; question: string; isCity: boolean }) => {
    const nonCity = mlOrdered.filter(x => !x.isCity).findIndex(x => x.id === it.id);
    const ph = it.isCity ? 'עיר' : nonCity === 0 ? 'מה קורה' : 'איפה'; // short placeholders (bughunter 02/10 #2); the question is the aria-label
    return (
      <button key={it.id} type="button" onClick={() => openBlank(it.i)} aria-haspopup="dialog" aria-label={`${it.question}${it.label ? ` — ${it.label}` : ''}`} data-ml-blank={it.i}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 3, verticalAlign: 'baseline', lineHeight: 1.35, background: 'none', border: 'none', borderBottom: '2px solid #1d4ed8', padding: '0 1px 1px', margin: 0, fontFamily: 'inherit', fontSize: 16, fontWeight: it.label ? 800 : 500, color: it.label ? '#1e3a8a' : '#64748b', cursor: 'pointer', whiteSpace: 'nowrap' }}>
        <span style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.label || ph}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
    );
  };


  return (
    <div ref={barRef} dir="rtl" data-qc-bar className="fixed inset-x-0 z-40" style={{ bottom: 0, paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {/* evening reassurance (Founder 14/10/26): one calm strip above the bar after the Israel evening window opens; renders nothing otherwise */}
      <EveningNote articleId={articleId} style={{ margin: 0, borderRadius: 0, borderInline: 'none', borderBottom: 'none', background: '#f8fafc', fontSize: 12.5, padding: '7px 14px', textAlign: 'center' }} />
      {sheetMode && pillOpen && <div aria-hidden="true" onClick={() => setPillOpen(false)} data-sheet-backdrop style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.38)', zIndex: -1 }} />}
      {collapsed && pillMode && (
        <div style={{ padding: '8px 12px 10px' }}>
          <button type="button" onClick={() => setPillOpen(true)} aria-expanded={false} aria-label={`פתיחת השאלון: ${pillLabel}`} data-bar-pill
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: preciseCtx ? 'linear-gradient(135deg, #12864A 0%, #0F7A43 100%)' : '#1d4ed8', color: '#fff', border: 'none', borderRadius: 999, padding: '12px 18px', fontWeight: 800, fontSize: 15, fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 8px 20px rgba(29,78,216,.35)' }}>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{preciseCtx ? '✅ ' : '📝 '}{pillLabel}</span>
            <b style={{ flexShrink: 0, background: 'rgba(255,255,255,.2)', borderRadius: 999, padding: '2px 10px', fontSize: 13 }}>{pillCount > 0 ? <><bdi dir="ltr">{pillCount}</bdi> תשובות ‹</> : 'פתיחה ‹'}</b>
          </button>
        </div>
      )}
      {collapsed && barVariant === 'sheetpill' && (
        <div style={{ padding: '6px 12px 10px' }}>
          <button type="button" onClick={() => setPillOpen(true)} aria-haspopup="dialog" aria-expanded={false} data-bar-pill
            style={{ width: '100%', minHeight: 46, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: preciseCtx ? '#0f7a43' : '#0f172a', color: '#fff', border: 'none', borderRadius: 999, padding: '0 20px', fontWeight: 700, fontSize: 15, fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 6px 18px rgba(15,23,42,.28)' }}>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pillLabel}</span>{chevUp}
          </button>
        </div>
      )}
      {collapsed && barVariant === 'sheetfooter' && (
        <div style={{ background: '#fff', borderTop: '1px solid rgba(15,23,42,.10)', display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', minHeight: 72, boxSizing: 'border-box' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>{preciseCtx ? 'כל השאלות נענו' : <>שאלה <bdi dir="ltr">{Math.min((mlProg?.step ?? 0) + 1, totalQ || 1)}</bdi> מתוך <bdi dir="ltr">{totalQ || 1}</bdi></>}</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pillLabel}</div>
          </div>
          <button type="button" onClick={() => setPillOpen(true)} aria-haspopup="dialog" aria-expanded={false} data-bar-pill
            style={{ flexShrink: 0, height: 44, padding: '0 18px', borderRadius: 999, border: 'none', background: preciseCtx ? '#0f7a43' : '#1d4ed8', color: '#fff', fontSize: 15, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}>
            {preciseCtx ? 'לשליחה' : 'ענו בשני קליקים'}
          </button>
        </div>
      )}
      {madlibs && !pillOpen && (
        <div style={{ background: '#fff', borderTop: '1px solid #0f172a', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', minHeight: 76, boxSizing: 'border-box' }} data-madlibs>
          <p style={{ flex: 1, minWidth: 0, margin: 0, fontSize: 16, fontWeight: 500, color: '#0f172a', lineHeight: 1.7, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            יש לי {mlOrdered.filter(it => !it.isCity).map((it, k, arr) => <span key={it.id}>{blank(it)}{k < arr.length - 1 ? ',' : ''} </span>)}
            {mlOrdered.some(it => it.isCity) && <span style={{ whiteSpace: 'nowrap' }}>ב{blank(mlOrdered.find(it => it.isCity)!)}</span>}
          </p>
          <button type="button" aria-label={preciseCtx ? 'שליחת הפנייה למומחים' : 'השלמת הפנייה'} data-ml-send
            onClick={() => { if (preciseCtx) handlePreciseSend(); else { const nx = mlItems.findIndex(it => !it.label); openBlank(nx >= 0 ? nx : 0); } }}
            style={{ flexShrink: 0, width: 44, height: 44, borderRadius: '50%', border: 'none', background: preciseCtx ? '#0f7a43' : '#0f172a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"><path d="M19 12H5M11 6l-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>
      )}
      <div hidden={collapsed || (madlibs && !pillOpen)} ref={sheetRef} tabIndex={sheetMode ? -1 : undefined} role={sheetMode ? 'dialog' : undefined} aria-modal={sheetMode ? true : undefined} aria-label={sheetMode ? 'שאלון קצר' : undefined}
        style={{ background: 'rgba(255,255,255,.98)', backdropFilter: 'blur(12px)', borderTop: v3 ? (barVariant === 'seg' ? '1px solid #0f172a' : 'none') : '1px solid #e2e8f0', boxShadow: v3 ? (sheetMode ? '0 -8px 24px rgba(15,23,42,.14)' : 'none') : '0 -4px 20px rgba(13,71,161,.12)', position: 'relative', ...(sheetMode ? { borderRadius: '20px 20px 0 0', paddingTop: 22, outline: 'none', maxWidth: 640, marginInline: 'auto' } : {}) }}>
        {sheetMode && (
          <button type="button" onClick={() => setPillOpen(false)} aria-label="סגירת השאלון" data-sheet-close
            style={{ position: 'absolute', top: 0, insetInline: 0, margin: '0 auto', width: 80, height: 22, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            <span aria-hidden="true" style={{ display: 'block', width: 36, height: 4, borderRadius: 2, background: '#cbd5e1', margin: '9px auto 0' }} />
          </button>
        )}
        {pillMode && (
          <button type="button" onClick={() => setPillOpen(false)} aria-expanded={true} aria-label="מזעור השאלון" data-bar-collapse
            style={{ position: 'absolute', top: -30, left: 12, height: 30, padding: '0 14px', borderRadius: '12px 12px 0 0', background: 'rgba(255,255,255,.98)', border: '1px solid #e2e8f0', borderBottom: 'none', color: '#475569', fontWeight: 800, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer' }}>
            מזעור ▾
          </button>
        )}
        {/* Full-bleed at every width (2026-08-22 user request) — a mid-size desktop
            browser window (under the old 1024px lg breakpoint, which is most windows
            unless maximized on a wide monitor) used to keep the max-w-3xl centered cap,
            showing empty white margins on both sides that never appear on an actual
            phone. Must look 100% identical on phone and desktop — always full width. */}
        <div className="w-full" style={{ position: 'relative' }}>

          {/* No dismiss/close control (2026-07-23 user request) — once seeded, the bar
              stays. In questions mode, the corner spot a close button would occupy is
              used for back/skip navigation instead (QuestionnaireWizard's
              condensedHeaderBg branch). */}

          {/* The element's shape never changes between modes: a header band, then ONE
              content row, then the action buttons. Only what's INSIDE those slots swaps —
              2026-07-23 user request. */}
          {mode === 'search' ? (
            <div className="flex items-center justify-center gap-1.5 flex-wrap text-center px-3 py-2" style={{ background: headerGradient }}>
              <span className="inline-block px-2.5 py-0.5 rounded-full font-black text-white" style={{ background: '#059669', fontSize: 12.5 }}>חינם</span>
              <span className="font-black" style={{ fontSize: 14.5, color: '#fff' }}>
                {singlePro ? `ייעוץ מהיר עם ${singlePro.firstName}` : `ייעוץ מהיר עם ${categoryNamePlural || categoryName} מומלצים לידך`}
              </span>
            </div>
          ) : preciseCtx && !madlibs && (compactBar || sheetMode) ? (
            // ?layout= compact ready step: ONE row — back to Q1 · the matched experts' faces · send (same handlers as below)
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', minHeight: 79 /* bar = this + 1 = the compact question bar's 80px (patrol 02/10 r3: 73 gave 74) */, boxSizing: 'border-box' }} data-bar-ready>
              {preciseQuestionnaire && (
                <button type="button" onClick={handlePreciseRestart} title="שינוי תשובות" aria-label="שינוי תשובות"
                  style={{ flexShrink: 0, width: 26, height: 40, border: 'none', background: 'transparent', color: '#1d4ed8', fontSize: 26, fontWeight: 900, cursor: 'pointer', padding: 0, lineHeight: 1 }}>‹</button>
              )}
              {!narrowing && shownPros.length > 0 && (
                <div style={{ display: 'flex', flexShrink: 0 }} aria-hidden="true">
                  {shownPros.slice(0, 3).map((p, i) => (
                    <img key={p.id} {...avatarImg(p.imgSrc, 30)} alt="" width={30} height={30} loading="lazy" decoding="async"
                      style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', border: '2px solid #fff', marginInlineStart: i ? -10 : 0, boxShadow: '0 1px 3px rgba(15,23,42,.18)' }} />
                  ))}
                </div>
              )}
              <button onClick={handlePreciseSend} type="button"
                className="rounded-xl text-white font-bold"
                style={{ flex: 1, minWidth: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: v3 ? '#0f172a' : 'linear-gradient(135deg, #12864A 0%, #0F7A43 100%)', border: 'none', cursor: 'pointer', padding: v3 ? '12px 10px' : '11px 10px', fontSize: v3 ? 16 : 13.5, fontWeight: v3 ? 800 : undefined }}>
                {!v3 && <WaIcon size={14} />}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v3 ? 'שלחו לי מומחים' : singlePro ? `שלח את הפנייה ל${singlePro.firstName} וקבל הצעה בחינם` : 'שלח את הפנייה למומחים וקבל הצעות בחינם'}</span>
              </button>
            </div>
          ) : preciseCtx && !madlibs ? (
            // Questionnaire done — same two slots (header band + content row) the
            // question/chips occupied, now holding the "ready to send" state instead, so
            // the element's height never jumps at the end either (user request).
            // 2026-08-23 (user report, live-measured via the bar's own `--wa-bar-h` CSS
            // var, not calculated): this content div's height was still 154 — correct
            // for the OLD taller 48px vertical-avatar-card preview style, but the
            // 2026-08-23 avatar-style unification (matching the compact mid-questionnaire
            // row, see below) shrank the actual preview content without shrinking this
            // fixed wrapper to match, so the bar grew ~26px (188px mid-questionnaire →
            // 214px at finish) the instant the questionnaire completed — exactly the
            // "static height, never jumps, not even at the end" invariant this whole
            // section exists to satisfy. Re-measured down to 128, which brings the
            // finish-state total back to the same 214-26=188px every other step already
            // used. If this preview's item markup ever changes size again, RE-MEASURE
            // live (`--wa-bar-h` before vs after the last question) rather than guessing
            // a new fixed number — see [[feedback_questionnaire_engine_fixed_height]].
            <>
              <div className="flex items-center justify-center px-3 py-2.5" style={{ background: headerGradient }}>
                <span className="font-black" style={{ fontSize: 15.5, color: '#fff' }}>
                  {singlePro ? `הפנייה האישית שלך מוכנה לשליחה אל ${singlePro.firstName}` : 'הפנייה מוכנה לשליחה'}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '6px 15px', height: 76 /* = the wizard's content slot (119 − 43 band), measured live 02/10 */, boxSizing: 'border-box' }}>
                {/* 2026-07-29 user request: the button never swaps to a "נשלח" state —
                    it stays clickable as-is even after sending. */}
                <button onClick={handlePreciseSend} type="button"
                  className="w-full rounded-xl text-white font-bold flex-shrink-0"
                  style={{ position: 'relative', background: 'linear-gradient(135deg, #12864A 0%, #0F7A43 100%)', border: 'none', cursor: 'pointer', padding: '10px 14px', fontSize: 13, textAlign: 'center' }}>
                  <span
                    style={{
                      position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)',
                      width: 24, height: 24, borderRadius: '50%', background: 'rgba(255,255,255,.22)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}
                  >
                    <WaIcon size={13} />
                  </span>
                  {singlePro ? `שלח את הפנייה ל${singlePro.firstName} וקבל הצעה בחינם` : 'שלח את הפנייה למומחים וקבל הצעות בחינם'}
                </button>
                {/* bughunter 2026-09-30: the ready step had no way back. Remount the wizard at Q1 with answers
                    cleared (same nonce the city-reset uses). Fixed 18px row inside the fixed 128px slot — the
                    pros row below is flex:1/minHeight:0 and absorbs it, so the bar's height never changes. */}
                {preciseQuestionnaire && (
                  <button type="button" onClick={handlePreciseRestart} className="qc-restart"
                    style={{ position: 'relative', alignSelf: 'flex-start', flexShrink: 0, height: 18, padding: 0, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11.5, fontWeight: 700, color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: 3, lineHeight: 1 }}>
                    <span aria-hidden="true" style={{ fontSize: 13, lineHeight: 1 }}>→</span> שינוי תשובות
                  </button>
                )}
                {preciseQuestionnaire && (
                  /* bughunter 02/10 #4: 82x18 tap target → invisible hit area (3px up = half the gap to the send button, 6px down, 12px sides); visuals unchanged */
                  <style>{'.qc-restart::before{content:"";position:absolute;inset:-3px -12px -6px}'}</style>
                )}
                {/* 2026-08-14 fix (user report): was gated on `cityName`, so categories
                    with skipCityQuestion (no city step in their questionnaire — remote/
                    nationwide services) never set cityName and this row silently stayed
                    empty forever, leaving dead reserved space under the send button.
                    shownPros already defaults to the category's top pros (unfiltered)
                    until/unless a city narrows it — safe to show any time it's populated. */}
                {/* 2026-08-23 (user report): this used to render a bigger/centered
                    vertical-card style (48px avatar, name below, centered column) that
                    visually differed from the compact 34px horizontal-row style shown
                    DURING the questionnaire (mode==='questions', below) — the mismatch
                    itself wasn't the bug, but it's what made the surrounding bar's total
                    height shift when transitioning between the two states. Now uses the
                    exact same item markup as that compact mid-questionnaire row so the
                    pro preview never changes shape between "answering" and "ready to
                    send" — only the button above it changes. */}
                {/* 02/10/26 (Founder): the pros row that sat here is removed with the searching strip — space reclaimed. */}
              </div>
            </>
          ) : (
            preciseQuestionnaire && (
              <QuestionnaireWizard
                // 2026-08-23 (user report): navigating pro A → category → pro B left the
                // questionnaire addressed to A — this bar's OWN state (mode/preciseCtx/
                // shownPros) correctly reset on the new seedKey (see the effect above),
                // but QuestionnaireWizard is a separate stateful child whose internal
                // step/answerMap/done never depended on seedKey at all, only on
                // cityResetNonce (city-reset only, unrelated to pro/category identity).
                // A visitor who'd finished A's questionnaire landed on B still showing
                // A's "ready to send" done-state (no send button, just a restart link);
                // one mid-flow landed on B resuming A's partial answers. Keying on
                // `seedKey` too forces a genuine remount — fresh step/answers/done —
                // every time the bar is re-seeded for a different pro OR category, while
                // a same-context re-seed (same seedKey) still doesn't reset anything the
                // visitor already did on THIS page.
                key={`${seedKey}:${cityResetNonce}`}
                questionnaire={preciseQuestionnaire}
                categoryName={categoryName}
                skipCityQuestion={skipCityQuestion}
                cityLast={!!cityLast}
                presetCity={knownCity || undefined}
                multi
                condensed
                condensedHeaderBg={headerGradient}
                condensedHeaderColor="#fff"
                barLayout={wizardLayout}
                onProgress={onWizardProgress}
                jump={mlJump}
                onComplete={handlePreciseComplete}
                onCityAnswered={applyCity}
                singleProName={singlePro?.firstName}
                proCity={singlePro ? ((singlePro as any).proCity ?? null) : undefined} // pro page: his city is the first chip (re-verify #7)
                singleProCoords={singlePro && singlePro.lat != null && singlePro.lng != null ? { lat: singlePro.lat, lng: singlePro.lng } : null}
                singleProService={singlePro ? { serviceCities: singlePro.serviceCities ?? null, serviceRadiusKm: singlePro.serviceRadiusKm ?? null, serviceMode: (singlePro as any).serviceMode ?? null } as any : null}
              />
            )
          )}

          {!compactBar && !sheetMode && mode !== 'questions' && <div className="px-3 pt-2 pb-2">
            {/* Founder 02/10/26: the "מחפש מומחים" searching strip (marquee + rotating status) is REMOVED from every questionnaire
                template — it showed motion, not information. Supersedes the 30/09 strip law (lib/searchStrip.ts kept for the faces door). */}
            {/* 02/10 (patrol): the 46px slot the strip used now shows the matched experts, STATIC — information, not motion — so the
                bar keeps the same height on every step through the ready step (the fixed-height invariant below). */}
            {/* Founder 02/10/26 (2nd order: "תוריד את האנימציה של חיפוש מומחים… היא סתם תופסת מקום"): the static matched-pros row
                that replaced the strip is GONE too, and this wrapper no longer renders in questions mode (its 16px padding was empty
                space) — the full bar is 120px on every step (wizard 119 + 1px border; ready step re-fit to the same 76px slot). */}
            {mode === 'search' && (
              <>
                {/* ONE combined row — pros and cities share it (2026-07-23 user request,
                    same slot the questionnaire's answer chips occupy in questions mode):
                    searching → status text primary, a dense continuous carousel of real
                    pro photos faded behind it; picked → real pros (clickable through to
                    their own page — bar stays mounted, see module header) immediately
                    followed, same row, by the city chips. */}
                <div style={{ position: 'relative', height: 46, borderRadius: 12, overflow: 'hidden', background: '#f8fafc', border: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, height: '100%', overflowX: 'auto', padding: '0 10px', WebkitOverflowScrolling: 'touch' }}>
                      {shownPros.map(p => (
                        <Link key={p.id} href={proPath(p.id)} style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}>
                          <img {...avatarImg(p.imgSrc, 34, { kind: 'pro', ctx: { category: null, proName: p.firstName } })} width={34} height={34} loading="lazy" decoding="async"
                            style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e0e7ff', flexShrink: 0 }} />
                          <div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                              <span style={{ fontSize: 11.5, fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap' }}>{p.firstName}</span>
                              <span style={{ fontSize: 10.5, color: '#b45309', fontWeight: 700 }}>★{fmtRating(p.rating10)}</span>
                            </div>
                            <div style={{ fontSize: 9.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>{p.reviewCount} המלצות</div>
                          </div>
                        </Link>
                      ))}
                      <div style={{ width: 1, alignSelf: 'stretch', margin: '6px 2px', background: '#e2e8f0', flexShrink: 0 }} />
                      {cities.map(c => (
                        <button key={c} type="button" onClick={() => applyCity(c)}
                          style={{ ...cityChipStyle, ...(cityName === c ? { background: '#0d47a1', color: '#fff', borderColor: '#0d47a1' } : {}) }}>
                          {c}
                        </button>
                      ))}
                      <button type="button" onClick={() => setOtherOpen(o => !o)}
                        style={{ ...cityChipStyle, border: '1.5px dashed #cbd5e1', background: otherOpen ? '#eff6ff' : '#f8fafc', color: '#64748b' }}>
                        ✏️ אחר
                      </button>
                    </div>
                </div>

                {otherOpen && (
                  <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px 4px 10px', borderRadius: 12, border: '1.5px solid #93c5fd', background: '#eff6ff' }}>
                    <input ref={inputRef} value={otherText} onChange={e => setOtherText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && otherText.trim()) applyCity(otherText.trim()); }}
                      placeholder="הקלידו עיר…" autoFocus
                      style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 12.5, flex: 1, minWidth: 0, color: '#0f172a', fontFamily: 'inherit' }} />
                    {otherText.trim() && (
                      <button onClick={() => applyCity(otherText.trim())} type="button"
                        style={{ background: '#0d47a1', color: '#fff', border: 'none', borderRadius: 9, padding: '3px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>✓</button>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Bottom slot. 2026-07-29 (user request): the direct "straight to WhatsApp,
                no questionnaire" button is GONE whenever a questionnaire exists — the
                only path to sending is completing the questions (that's what makes the
                composed per-pro messages possible). It survives solely as the fallback
                CTA for categories that have no questionnaire at all. */}
            {preciseQuestionnaire ? ( /* the CTA belongs to search mode only (patrol 02/10 v2) — the wrapper renders in search mode only */
              <button onClick={() => setMode('questions')} type="button"
                className="w-full rounded-xl font-bold"
                style={{ background: '#eff6ff', color: '#1d4ed8', border: '1.5px solid #bfdbfe', cursor: 'pointer', padding: '10px 14px', fontSize: 14 }}>
                🎯 כמה שאלות קצרות — ונחבר אתכם למומחים המתאימים
              </button>
            ) : (
              <button onClick={handleSend} type="button"
                className="w-full rounded-xl text-white font-bold"
                style={{ position: 'relative', background: 'linear-gradient(135deg, #12864A 0%, #0F7A43 100%)', border: 'none', cursor: 'pointer', padding: '9px 14px', fontSize: 15, textAlign: 'center' }}>
                <span
                  style={{
                    position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)',
                    width: 24, height: 24, borderRadius: '50%', background: 'rgba(255,255,255,.22)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}
                >
                  <WaIcon size={13} />
                </span>
                {variant?.copyText || 'קבלת ייעוץ מיידי בחינם'}
              </button>
            )}
          </div>}
        </div>
      </div>
    </div>
  );
}
